import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { AdsSection } from '@/components/AdsSection/AdsSection';
import { Footer } from '@/components/Footer/Footer';
import { PageTopControls } from '@/components/PageTopControls/PageTopControls';
import { Breadcrumbs } from '@/components/Breadcrumbs/Breadcrumbs';
import { Button } from '@/components/UI/Button/Button';
import { HeartIcon, ShareIcon } from '@/components/UI/Icons/Icons';
import * as S from '@/views/Ads/Ad.styles';
import { MainInfo } from '@/components/Ad/MainInfo/MainInfo';
import { Divider } from '@/components/UI/Divider/Divider.styles';
import { Status } from '@/components/Ad/Status/Status';
import { Settings } from '@/components/Ad/Settings/Settings';
import { Properties } from '@/components/Ad/Properties/Properties';
import { BankPromo } from '@/components/Ad/BankPromo/BankPromo';
import { Description } from '@/components/Ad/Description/Description';
import { AuthorInfo } from '@/components/Ad/AuthorInfo/AuthorInfo';
import { Promotions } from '@/components/Ad/Promotions/Promotions';
import { Complaint } from '@/components/Ad/Complaint/Complaint';
import { Overview } from '@/components/Ad/Overview/Overview';
import { SliderWithGallery } from '@/components/Ad/SliderWithGallery/SliderWithGallery';
import { AdStatus } from '@/types';
import { ErrorView } from '@/views/Error/Error.view';
import { FloatingButtons } from '@/components/Ad/FloatingButtons/FloatingButtons';
import { useShareData } from '@/hooks/useShareData';
import { useCustomPush } from '@/components/CustomLink/CustomLink.hooks';
import {
  MAX_SHOP_RELATED_ADS,
  MAX_USER_RELATED_ADS,
} from '@/views/Ads/Ad.view.constants';
import { useAdDetails } from '@/views/Ads/useAdDetails';
import { WarningPendingState } from '@/components/Ad/WarningPendingState/WarningPendingState';
import { ShopBanner } from '@/components/Ad/ShopBanner/ShopBanner';
import { useIsAuthor } from '@/hooks/useIsAuthor';
import { useIsShop } from '@/hooks/useIsShop';
import { useBanner } from '@/hooks/useBanner';
import { GetUserResponse } from '@/api/types/user.types';
import { GET_USER } from '@/api/queries/user.queries';
import { WebViewPaymentStatusModal } from '@/components/Payment/WebViewPaymentStatusModal/WebViewPaymentStatusModal';
import { PaymentInTerminalsAfterAuth } from '@/components/Payment/PaymentInTerminalsAfterAuth';
import { useCurrentCategoryDetails } from '@/hooks/useCurrentCategory/useCurrentCategoryDetails';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import { Ad } from '@/api/types/common.types';
import { extractTargetParams } from '@/utils/helpers';
import { useServerDataContext } from '@/contexts';
import { useBookmark } from '@/hooks/useBookmark';

export const AdView = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const customPush = useCustomPush();
  const { adDetails, loading } = useAdDetails();
  const { initialAdDetails } = useServerDataContext();
  const shop = adDetails?.shop || initialAdDetails?.shop;

  const targetParams = useMemo(
    () => extractTargetParams(adDetails),
    [adDetails]
  );

  const { topBanner } = useBanner({
    targetParams: targetParams || {},
    canLoadTopBanner: !loading && !!targetParams,
  });
  const isAuthor = useIsAuthor();
  const isShop = useIsShop(initialAdDetails);

  const { data: userInfo } = useQuery<GetUserResponse>(GET_USER);

  const { categoryDetails } = useCurrentCategoryDetails();

  const status = adDetails?.status;
  const { shareUrl, canShare } = useShareData();
  const { isBookmarked: isFavorite, toggleBookmark } = useBookmark({
    ad: adDetails,
  });

  function showMore() {
    const moreAdsLink = isShop ? shop?.uri : `/${categoryDetails?.path}`;
    if (moreAdsLink) {
      customPush(moreAdsLink);
    }
  }

  const { Published, Expired } = AdStatus;
  const isExpiredOrPublished = status && [Published, Expired].includes(status);
  const canComplain =
    !isAuthor && isExpiredOrPublished && !userInfo?.currentUser?.isBanned;
  const canBookmark = status === Published || (status === Expired && isAuthor);
  const shouldShowNotFound = !adDetails || (!isAuthor && !isExpiredOrPublished);
  const userRoleCondition = isAuthor
    ? isExpiredOrPublished
    : status === Published;
  const isShareVisible = canShare && userRoleCondition;

  function getHeaderActions() {
    if (loading) {
      return (
        <S.AdHeaderActions>
          <SkeletonLoader width="17px" height="20px" />
          <SkeletonLoader width="20px" height="20px" />
        </S.AdHeaderActions>
      );
    }

    return (
      <S.AdHeaderActions>
        {isShareVisible && (
          <Button
            appearance="transparent"
            hasPadding={false}
            onClick={shareUrl}
          >
            <ShareIcon
              fill={theme.palette.primary.labels}
              className="share-btn js-share-btn"
            />
          </Button>
        )}

        {canBookmark && (
          <Button
            appearance="transparent"
            hasPadding={false}
            onClick={toggleBookmark}
            data-stat={
              !isFavorite && isShop ? 'shop-ad-bookmarking' : undefined
            }
            className={!isFavorite ? 'add_bookmark' : undefined}
          >
            <HeartIcon
              data-testid="bookmark-icon"
              fill={isFavorite ? theme.palette.brand.main : 'none'}
              stroke={
                isFavorite
                  ? theme.palette.brand.main
                  : theme.palette.primary.labels
              }
            />
          </Button>
        )}
      </S.AdHeaderActions>
    );
  }

  if (!loading && shouldShowNotFound) {
    return (
      <ErrorView
        title={t('pages.ad.nothing_found')}
        subtitle={t('pages.ad.nothing_found_ad_description')}
        hasTopBanner
      />
    );
  }

  const adsLimit = isShop ? MAX_SHOP_RELATED_ADS : MAX_USER_RELATED_ADS;
  const adsList = adDetails
    ? adDetails.relatedAds.slice(0, adsLimit)
    : Array.from({ length: MAX_USER_RELATED_ADS }, () => ({} as Ad));
  const hasShowMoreButton = isShop || adsList?.length >= adsLimit;
  const shouldShowBanner = !isShop && status !== AdStatus.Pending;
  const hasFloatingButtons = !isAuthor && status === AdStatus.Published;
  const shouldShowBurgerInHeader = status === AdStatus.Pending;

  return (
    <>
      {shouldShowBanner && topBanner}

      <PageTopControls
        hasLogoLink
        hasBurger={shouldShowBurgerInHeader}
        headerLeftAction={getHeaderActions()}
      />

      <Breadcrumbs hasHorizontalPadding hasBg />

      {isAuthor && status === AdStatus.Pending && <WarningPendingState />}

      {isShop && <ShopBanner shop={shop} />}

      <SliderWithGallery
        isBookmarked={isFavorite}
        toggleBookmark={toggleBookmark}
        canBookmark={canBookmark}
        {...(status === AdStatus.Expired &&
          !isAuthor && { overlayText: t('common.ad_inactive') })}
      />

      {status && <Status status={status} isAuthor={isAuthor} />}

      <S.AdViewMain>
        {isAuthor ? (
          <>
            <MainInfo />
            <Promotions />
            <Settings />
            <Properties />
            <BankPromo />
            <Description />
            <AuthorInfo />
            <Overview />
          </>
        ) : (
          <>
            <MainInfo />
            <Properties />
            <BankPromo />
            <Description />
            <AuthorInfo />
            <Overview />
            <Divider $isSmall />
            {canComplain && <Complaint />}
            <Promotions hasBottomMargin />
          </>
        )}
      </S.AdViewMain>

      <AdsSection
        ads={adsList}
        adsLoading={loading}
        chunkSize={adsLimit}
        title={
          adsList?.length
            ? t(`pages.ad.${isShop ? 'other_users_ads' : 'similar_ads'}`)
            : undefined
        }
        appearance="onlyTitle"
        onShowMoreClick={showMore}
        hasBottomButton={hasShowMoreButton}
        {...(isShop && {
          showMoreText: t('pages.ad.go_to_shop'),
        })}
      />

      {hasFloatingButtons && <FloatingButtons />}

      <Footer {...(hasFloatingButtons && { bottomGutter: '100px' })} />

      <PaymentInTerminalsAfterAuth adId={adDetails?.id} />

      <WebViewPaymentStatusModal />
    </>
  );
};
